import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useExchangeRate } from '@/hooks/useExchangeRate';
import { useStore } from '@/utils/store';
import { formatCurrency } from '@/utils/format';
import CurrencyInput from '@/components/ui/CurrencyInput';
import { useFormDraft } from '@/hooks/useFormDraft';
import { X, RefreshCw, AlertCircle } from 'lucide-react';

export default function ExchangeRateModal({ open, onClose, business }) {
  const { t } = useTranslation();
  const from = business?.currency || 'USD';
  const to = 'CLP';
  const { rate: apiRate, loading: rateLoading, error: rateError, refresh } = useExchangeRate(from, to);
  const registerExchange = useStore((s) => s.registerExchange);
  const draft = useFormDraft('exchangeRate');

  const [amount, setAmount] = useState('');
  const [rate, setRate] = useState('');
  const [useManualRate, setUseManualRate] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (apiRate && !useManualRate) {
      setRate(String(apiRate));
    }
  }, [apiRate, useManualRate]);

  useEffect(() => {
    if (open) {
      const saved = draft.load();
      if (saved) {
        setAmount(saved.amount || '');
        setDescription(saved.description || '');
        setDate(saved.date || new Date().toISOString().split('T')[0]);
      } else {
        setAmount('');
        setDescription('');
        setDate(new Date().toISOString().split('T')[0]);
      }
      setUseManualRate(false);
    }
  }, [open]);

  // Auto-save draft
  useEffect(() => {
    if (!open) return;
    draft.save({ amount, description, date });
  }, [open, amount, description, date]);

  if (!open) return null;

  const numAmount = parseFloat(amount) || 0;
  const numRate = parseFloat(rate) || 0;
  const result = numAmount * numRate;

  const handleSave = () => {
    if (numAmount <= 0 || numRate <= 0) return;
    draft.clear();
    registerExchange({
      amount: numAmount,
      rate: numRate,
      date,
      description: description || t('exchange.defaultDescription', { from, to }),
      businessId: business?.id,
      from,
      to,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-800">
          <h2 className="text-lg font-display font-bold text-gray-900 dark:text-white">
            {t('exchange.title')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Amount in source currency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('exchange.amount')} ({from})
            </label>
            <CurrencyInput
              value={amount}
              onChange={setAmount}
              currencyOverride={from}
              placeholder="0"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors"
              autoFocus
            />
          </div>

          {/* Exchange rate */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('exchange.rate')} (1 {from} = ? {to})
              </label>
              <button
                onClick={refresh}
                disabled={rateLoading}
                className="flex items-center gap-1 text-xs text-brand-600 dark:text-brand-400 hover:text-brand-700 transition-colors disabled:opacity-50"
              >
                <RefreshCw size={12} className={rateLoading ? 'animate-spin' : ''} />
                {t('exchange.refresh')}
              </button>
            </div>
            <input
              type="number"
              value={rate}
              onChange={(e) => {
                setRate(e.target.value);
                setUseManualRate(true);
              }}
              placeholder="0.00"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors"
              min="0"
              step="0.01"
            />
            {rateError && (
              <div className="flex items-center gap-1 mt-1 text-xs text-warning-600 dark:text-warning-400">
                <AlertCircle size={12} />
                {t('exchange.rateError')}
              </div>
            )}
            {useManualRate && (
              <button
                onClick={() => {
                  setUseManualRate(false);
                  if (apiRate) setRate(String(apiRate));
                }}
                className="text-xs text-brand-600 dark:text-brand-400 mt-1 hover:underline"
              >
                {t('exchange.useApiRate')}
              </button>
            )}
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('exchange.date')}
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('exchange.description')}
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('exchange.descriptionPlaceholder')}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors"
            />
          </div>

          {/* Result preview */}
          {numAmount > 0 && numRate > 0 && (
            <div className="p-4 rounded-xl bg-brand-50 dark:bg-brand-500/10 border border-brand-200 dark:border-brand-500/20">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('exchange.result')}</div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-danger-600 dark:text-danger-400 font-medium">
                  -{formatCurrency(numAmount, from)}
                </span>
                <span className="text-gray-400">→</span>
                <span className="text-lg font-bold text-success-600 dark:text-success-400">
                  +{formatCurrency(result, to)}
                </span>
              </div>
              <div className="text-[10px] text-gray-400 mt-1">
                {t('exchange.rateUsed')}: 1 {from} = {formatCurrency(numRate, to)}
              </div>
            </div>
          )}

          {/* Save button */}
          <button
            onClick={handleSave}
            disabled={numAmount <= 0 || numRate <= 0}
            className="w-full py-3 rounded-xl bg-brand-600 text-white font-semibold hover:bg-brand-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('exchange.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
